import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import BottleSilhouette from "./BottleSilhouette";
import GinImage from "./GinImage";
import DnaBars from "./DnaBars";
import { topDnaAxes, getCategoryColor } from "../lib/constants";

export default function GinCard({ gin, index = 0, onCompareToggle, isCompared = false }) {
  const top = topDnaAxes(gin.dna || gin.gin_dna, 4);
  const color = getCategoryColor(gin.category);
  const unavailable = gin.availability_status === "NOT_AVAILABLE" || gin.is_available === false;
  return (
    <motion.article
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45, delay: Math.min(index * 0.04, 0.4), ease: [0.16, 1, 0.3, 1] }}
      className={`group relative bg-[#15171A] border border-white/5 hover:border-white/20 transition-all duration-500 ${
        unavailable ? "opacity-60 saturate-[.35]" : ""
      }`}
      data-testid={`gin-card-${gin.id}`}
      data-availability={gin.availability_status || "AVAILABLE"}
    >
      <Link to={`/gin/${gin.id}`} className="block">
        {/* Bottle */}
        <div className="relative h-56 md:h-64 flex items-end justify-center overflow-hidden grain">
          <div className="absolute inset-0 opacity-30 transition-opacity duration-700 group-hover:opacity-60"
               style={{ background: `radial-gradient(circle at 50% 80%, ${color}22, transparent 70%)` }} />
          <motion.div
            whileHover={{ y: -6 }}
            transition={{ type: "spring", stiffness: 200, damping: 20 }}
            className="relative"
          >
            <GinImage gin={gin} size={84} />
          </motion.div>
          {/* Price chip */}
          <div className="absolute top-3 right-3 px-2 py-1 border text-[10px] tracking-[0.2em] uppercase"
               style={{ borderColor: color + "66", color: color }}>
            ₹{gin.price} / 30ml
          </div>
          {/* Country flag */}
          <div className="absolute top-3 left-3 text-[10px] uppercase tracking-[0.25em] text-white/60">
            {gin.country}
          </div>
          {/* Unavailable corner badge */}
          {unavailable && (
            <div
              className="absolute bottom-3 left-3 px-2.5 py-1 bg-[#0E0F0C]/85 border border-white/15 text-[9px] uppercase tracking-[0.22em] text-white/80 backdrop-blur-sm"
              data-testid={`unavail-badge-${gin.id}`}
            >
              Not on tonight's menu
            </div>
          )}
        </div>

        <div className="divider-rule" />

        <div className="p-5 space-y-3">
          <header>
            <h3 className="font-display text-2xl leading-none">{gin.name}</h3>
            <p className="text-xs text-white/50 mt-1">{gin.styles?.slice(0, 2).join(" · ")} · {gin.abv}% ABV</p>
          </header>

          <p className="text-sm text-white/70 leading-relaxed line-clamp-2">{gin.hook}</p>

          {/* Top mood tags */}
          <div className="flex flex-wrap gap-1.5">
            {gin.moods?.slice(0, 3).map((m) => (
              <span key={m} className="text-[10px] uppercase tracking-[0.18em] text-white/50 border border-white/10 px-2 py-0.5">
                {m}
              </span>
            ))}
          </div>

          {/* DNA */}
          <div className="pt-1">
            <p className="overline text-white/40 mb-2">BENO Gin DNA</p>
            <DnaBars axes={top} category={gin.category} compact />
          </div>
        </div>
      </Link>

      {/* Compare toggle — disabled on unavailable */}
      {onCompareToggle && !unavailable && (
        <button
          onClick={(e) => { e.preventDefault(); onCompareToggle(gin.id); }}
          className={`absolute bottom-3 right-3 text-[10px] uppercase tracking-[0.2em] border px-2 py-1 transition-colors ${
            isCompared ? "bg-[#C17F59] text-[#0E0F0C] border-[#C17F59]" : "border-white/15 text-white/50 hover:text-[#C17F59] hover:border-[#C17F59]"
          }`}
          data-testid={`compare-toggle-${gin.id}`}
        >
          {isCompared ? "Added" : "Compare"}
        </button>
      )}
    </motion.article>
  );
}
